import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  Input,
  Select,
  VStack,
  Badge,
  Spinner,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import { MdPlayArrow, MdApi } from "react-icons/md";
import { useAuth } from "contexts/UserContext";
import { useNavigate } from "react-router-dom";

// Each API now has `params: [{ key, placeholder, defaultValue }]`
// instead of the old single `param` + `input` fields.
const collections = [
  {
    name: "Downloaders",
    apis: [
      {
        name: "Instagram Downloader",
        method: "GET",
        path: "/api/downloader/igdl",
        status: "active",
        params: [
          {
            key: "url",
            placeholder: "Enter Instagram URL",
            defaultValue: "https://www.instagram.com/reel/DW0bFv5kaO9/?igsh=enJpY255cGJ3NWgy",
          },
        ],
      },
      {
        name: "Spotify Downloader",
        method: "GET",
        path: "/api/downloader/spotify",
        status: "active",
        params: [
          {
            key: "url",
            placeholder: "Enter Spotify track URL",
            defaultValue: "https://open.spotify.com/track/3MuPhiosfAjTmBO7QAYYmU?si=6be3342f9a3e469c",
          },
        ],
      },
      {
        name: "Tik Tok Downloader",
        method: "GET",
        path: "/api/downloader/tiktok",
        status: "active",
        params: [
          {
            key: "url",
            placeholder: "Enter TikTok URL",
            defaultValue: "https://vt.tiktok.com/ZSNvs6h6o",
          },
        ],
      },
      {
        name: "Terabox Downloader",
        method: "GET",
        path: "/api/downloader/terabox",
        status: "active",
        params: [
          {
            key: "url",
            placeholder: "Enter Terabox URL",
            defaultValue: "https://1024terabox.com/s/1uZR_Pj_NbMaalhpgTpRh1A",
          },
        ],
      },
      {
        name: "Facebook Downloader",
        method: "GET",
        path: "/api/downloader/facebook",
        status: "active",
        params: [
          {
            key: "url",
            placeholder: "Enter Facebook URL",
            defaultValue: "https://www.facebook.com/share/r/1aKYmoUuef",
          },
        ],
      },
      {
        name: "Gdrive Downloader",
        method: "GET",
        path: "/api/downloader/gdrive",
        status: "active",
        params: [
          {
            key: "url",
            placeholder: "Enter Google Drive URL",
            defaultValue: "https://drive.google.com/file/d/1B93NXx3_nClSRRMArlsFtwEDr2KCqLeV/view?usp=drivesdk",
          },
        ],
      },
      {
        name: "Threads Downloader",
        method: "GET",
        path: "/api/downloader/threads",
        status: "active",
        params: [
          {
            key: "url",
            placeholder: "Enter Threads URL",
            defaultValue: "https://www.threads.com/@iblameabhiijith/post/DW7_N2fkgOg?xmt=AQF0RxLnoPjvNOlcstIxkJ-E_wFeElwzI266-aYJizSZtOsVSD6tphkowplvd85MgM6fsVZW&slof=1",
          },
        ],
      },
    ],
  },
  {
    name: "Search",
    apis: [
      {
        name: "YouTube Search",
        method: "GET",
        path: "/api/search/yts",
        status: "active",
        params: [
          {
            key: "q",
            placeholder: "Enter anything to search",
            defaultValue: "Alan Walker faded"
          }
        ]
      }
    ]
  },
  {
    name: "Converter",
    apis: [
      {
        name: "Image Converter",
        method: "GET",
        path: "/api/convert",
        status: "disabled",
        params: [],
      },
    ],
  },
  {
    name: "AI Tools",
    apis: [
      {
        name: "AI Chat",
        method: "POST",
        path: "/api/chat",
        status: "disabled",
        params: [],
      },
    ],
  },
];

// ─── helpers ────────────────────────────────────────────────────────────────

function buildQueryString(params, paramValues) {
  const qs = new URLSearchParams();
  (params || []).forEach(({ key, defaultValue }) => {
    const val = paramValues?.[key];
    if (val !== undefined && val !== "") qs.append(key, val);
  });
  const str = qs.toString();
  if (str) return `?${str}`;
  // fallback placeholder shown in snippets when fields are empty
  const fallback = (params || []).map((p) => `${p.key}=VALUE`).join("&");
  return fallback ? `?${fallback}` : "";
}

// ─── component ───────────────────────────────────────────────────────────────

export default function Collections() {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const codeBg = useColorModeValue("gray.100", "gray.900");
  const codeText = useColorModeValue("gray.800", "green.300");
  const itemHover = useColorModeValue("gray.50", "whiteAlpha.50");
  const itemActive = useColorModeValue("gray.100", "whiteAlpha.100");
  const bannerBg = useColorModeValue("blue.50", "whiteAlpha.100");
  const bannerBorder = useColorModeValue("blue.200", "whiteAlpha.200");
  const bannerTitle = useColorModeValue("blue.800", "white");
  const bannerDesc = useColorModeValue("blue.600", "gray.400");

  const [selectedApi, setSelectedApi] = useState(null);
  const [paramValues, setParamValues] = useState({}); // { [key]: value }
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const API_KEY = user?.apiKey || "YOUR_API_KEY";

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      status: "success",
      duration: 1500,
    });
  };

  // Seed paramValues from each param's defaultValue when an API is selected
  const handleSelectApi = (api) => {
    setSelectedApi(api);
    const initial = {};
    (api.params || []).forEach((p) => {
      initial[p.key] = p.defaultValue || "";
    });
    setParamValues(initial);
    setResponse(null);
  };

  const handleParamChange = (key, value) => {
    setParamValues((prev) => ({ ...prev, [key]: value }));
  };

  const generateCurl = () => {
    const url = selectedApi?.path || "";
    const qs = buildQueryString(selectedApi?.params, paramValues);
    const fullUrl = `${window.location.origin}${url}`;
    return `curl -X ${selectedApi?.method || "GET"} "${fullUrl}${qs}" \\
  -H "Sparky-Api-Key: ${API_KEY || ""}" \\
  -H "Accept: application/json"`;
  };

  const generateNode = () => {
    const url = selectedApi?.path || "";
    const qs = buildQueryString(selectedApi?.params, paramValues);
    const fullUrl = `${window.location.origin}${url}`;
    return `const res = await fetch("${fullUrl}${qs}", {
  headers: {
    "Sparky-Api-Key": "${API_KEY || ""}",
    "Accept": "application/json"
  }
})
  const data = await res.json();
  console.log(data);`;
  };

  const isGuest = !user;

  const handleRunRequest = async () => {
    if (!selectedApi) return;
    if (isGuest) {
      toast({
        title: "Login Required",
        description: "Sign in to execute API requests.",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    setLoading(true);
    setResponse(null);
    try {
      const qs = buildQueryString(selectedApi.params, paramValues);
      const fullUrl = `${window.location.origin}${selectedApi.path}${qs}`;

      const res = await fetch(fullUrl, {
        method: selectedApi.method,
        headers: {
          "Sparky-Api-Key": API_KEY || "",
          Accept: "application/json",
        },
      });
      const data = await res
        .json()
        .catch(() => ({ message: "Could not parse JSON. Check Network tab." }));
      setResponse(data);
    } catch (err) {
      setResponse({ error: "Failed to fetch: " + err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* GUEST LOGIN BANNER */}
      {isGuest && (
        <Flex
          mb="20px"
          p="16px 20px"
          bg={bannerBg}
          borderRadius="14px"
          align="center"
          justify="space-between"
          border="1px solid"
          borderColor={bannerBorder}
        >
          <Box>
            <Text color={bannerTitle} fontSize="sm" fontWeight="700">
              🔒 You're viewing Collections in demo mode
            </Text>
            <Text color={bannerDesc} fontSize="xs">
              Login to use your real API key and execute live requests.
            </Text>
          </Box>
          <Button
            size="sm"
            colorScheme="blue"
            borderRadius="10px"
            onClick={() => navigate("/auth/sign-in")}
          >
            Sign In
          </Button>
        </Flex>
      )}

      <Grid
        mb="20px"
        gridTemplateColumns={{ xl: "repeat(3, 1fr)", "2xl": "1fr 1fr" }}
        gap={{ base: "20px", xl: "20px" }}
        display={{ base: "block", xl: "grid" }}
      >
        {/* LEFT PANEL */}
        <Flex
          flexDirection="column"
          gridArea={{ xl: "1 / 1 / 2 / 2", "2xl": "1 / 1 / 2 / 2" }}
        >
          <Text color={textColor} fontSize="2xl" fontWeight="700" mb="4px">
            Collections
          </Text>
          <Text color="gray.500" fontSize="md" mb="20px">
            Browse and run available API endpoints
          </Text>

          <Card p="20px" mb="20px">
            <Flex mb="20px" gap="10px">
              <Input
                placeholder="Search APIs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Select
                w="150px"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="All">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </Flex>

            <VStack align="stretch" spacing="20px">
              {collections.map((group, i) => {
                const filteredApis = group.apis.filter(
                  (api) =>
                    api.name.toLowerCase().includes(search.toLowerCase()) &&
                    (filter === "All" || api.status === filter)
                );

                if (filteredApis.length === 0) return null;

                return (
                  <Box key={i}>
                    <Text
                      color={textColor}
                      fontSize="lg"
                      fontWeight="600"
                      mb="10px"
                    >
                      {group.name}
                    </Text>
                    <VStack align="stretch" spacing="10px">
                      {filteredApis.map((api, j) => (
                        <Flex
                          key={j}
                          p="15px"
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius="12px"
                          alignItems="center"
                          justifyContent="space-between"
                          cursor="pointer"
                          bg={
                            selectedApi?.name === api.name
                              ? itemActive
                              : "transparent"
                          }
                          _hover={{ bg: itemHover }}
                          onClick={() => handleSelectApi(api)}
                          transition="all 0.2s"
                        >
                          <Flex
                            alignItems="center"
                            gap="15px"
                            overflow="hidden"
                          >
                            <Badge
                              colorScheme={
                                api.method === "GET" ? "blue" : "green"
                              }
                            >
                              {api.method}
                            </Badge>
                            <VStack align="start" spacing="2px">
                              <Text
                                color={textColor}
                                fontWeight="600"
                                fontSize="sm"
                                isTruncated
                                maxW="150px"
                              >
                                {api.name}
                              </Text>
                              <Text
                                color="gray.400"
                                fontSize="xs"
                                isTruncated
                                maxW="150px"
                              >
                                {api.path}
                              </Text>
                            </VStack>
                          </Flex>
                          <Flex alignItems="center" gap="15px">
                            <Box
                              w="8px"
                              h="8px"
                              borderRadius="50%"
                              bg={
                                api.status === "active"
                                  ? "green.400"
                                  : "red.400"
                              }
                            />
                            <Button
                              colorScheme="brand"
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectApi(api);
                              }}
                              isDisabled={api.status !== "active"}
                            >
                              <Icon as={MdPlayArrow} w="20px" h="20px" />
                            </Button>
                          </Flex>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                );
              })}
            </VStack>
          </Card>
        </Flex>

        {/* RIGHT PANEL */}
        <Flex
          flexDirection="column"
          gridArea={{ xl: "1 / 2 / 2 / 4", "2xl": "1 / 2 / 2 / 3" }}
        >
          {selectedApi ? (
            <Card p="20px" h="100%">
              <Text color={textColor} fontSize="xl" fontWeight="700" mb="20px">
                {selectedApi.name}{" "}
                <Badge
                  ms="10px"
                  colorScheme={selectedApi.method === "GET" ? "blue" : "green"}
                >
                  {selectedApi.method}
                </Badge>
              </Text>

              <Flex alignItems="center" justifyContent="space-between" mb="5px">
                <Text color={textColor} fontWeight="600">
                  Request URL
                </Text>
                <Button
                  size="xs"
                  onClick={() =>
                    copyToClipboard(
                      `${window.location.origin}${selectedApi?.path || ""}`
                    )
                  }
                >
                  Copy
                </Button>
              </Flex>
              <Box
                p="12px"
                bg={codeBg}
                borderRadius="8px"
                mb="20px"
                color={codeText}
                fontSize="sm"
                fontFamily="monospace"
              >
                {window.location.origin}
                {selectedApi?.path || ""}
              </Box>

              <Text color={textColor} fontWeight="600" mb="15px">
                Parameters
              </Text>

              {/* ── CHANGED: one Input row per param instead of a single hardcoded URL field ── */}
              <VStack spacing="15px" mb="20px">
                {(selectedApi.params || []).map(({ key, placeholder }) => (
                  <Box key={key} w="100%">
                    <Text color="gray.500" fontSize="sm" mb="5px">
                      {key} parameter
                    </Text>
                    <Input
                      placeholder={placeholder || `Enter ${key}...`}
                      value={paramValues[key] || ""}
                      onChange={(e) => handleParamChange(key, e.target.value)}
                      isReadOnly={isGuest}
                      opacity={isGuest ? 0.6 : 1}
                    />
                  </Box>
                ))}

                {/* API Key field — untouched */}
                <Box w="100%">
                  <Flex justify="space-between" mb="5px">
                    <Text color="gray.500" fontSize="sm">
                      API Key
                    </Text>
                    <Button
                      size="xs"
                      onClick={() => copyToClipboard(user?.apiKey)}
                    >
                      Copy
                    </Button>
                  </Flex>
                  <Input
                    placeholder="API Key"
                    value={API_KEY}
                    isReadOnly
                  />
                  {!user && (
                    <Flex
                      mt="10px"
                      p="10px"
                      bg="orange.50"
                      borderRadius="8px"
                      align="center"
                      justify="space-between"
                      border="1px solid"
                      borderColor="orange.200"
                    >
                      <Text color="orange.700" fontSize="xs" fontWeight="600">
                        Login to use your real API key
                      </Text>
                      <Button
                        size="xs"
                        colorScheme="orange"
                        onClick={() => navigate("/auth/sign-in")}
                      >
                        Login Now
                      </Button>
                    </Flex>
                  )}
                </Box>
              </VStack>

              {/* Tabs — untouched */}
              <Tabs
                variant="soft-rounded"
                colorScheme="brand"
                mb="20px"
                size="sm"
              >
                <TabList mb="10px">
                  <Tab>Snippet (Node.js)</Tab>
                  <Tab>Snippet (cURL)</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel p="0">
                    <Box
                      p="15px"
                      bg={codeBg}
                      borderRadius="12px"
                      color={codeText}
                      overflowX="auto"
                    >
                      <pre
                        style={{ fontSize: "12px", fontFamily: "monospace" }}
                      >
                        {generateNode()}
                      </pre>
                    </Box>
                  </TabPanel>
                  <TabPanel p="0">
                    <Box
                      p="15px"
                      bg={codeBg}
                      borderRadius="12px"
                      color={codeText}
                      overflowX="auto"
                    >
                      <pre
                        style={{ fontSize: "12px", fontFamily: "monospace" }}
                      >
                        {generateCurl()}
                      </pre>
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>

              {/* Send button */}
              <Button
                colorScheme={isGuest ? "gray" : "brand"}
                mb="30px"
                w={{ base: "100%", md: "fit-content" }}
                onClick={handleRunRequest}
                isLoading={loading}
                loadingText="Sending..."
                leftIcon={<Icon as={MdPlayArrow} />}
                isDisabled={isGuest || selectedApi.status !== "active"}
              >
                {isGuest ? "Login to Send" : "Send Request"}
              </Button>

              {/* Response box — untouched */}
              <Text color={textColor} fontWeight="600" mb="10px">
                Response
              </Text>
              <Box
                p="15px"
                bg={codeBg}
                borderRadius="12px"
                color={codeText}
                minH="200px"
                overflow="auto"
                position="relative"
              >
                {loading && (
                  <Flex
                    position="absolute"
                    top="0"
                    left="0"
                    w="100%"
                    h="100%"
                    bg="var(--chakra-colors-blackAlpha-600)"
                    align="center"
                    justify="center"
                    borderRadius="12px"
                    zIndex="10"
                  >
                    <Spinner color="brand.500" size="xl" />
                  </Flex>
                )}
                <pre
                  style={{
                    fontSize: "13px",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {response
                    ? JSON.stringify(response, null, 2)
                    : "Click 'Send Request' to view the response..."}
                </pre>
              </Box>
            </Card>
          ) : (
            <Card p="40px" h="100%" align="center" justify="center">
              <Icon as={MdApi} w="60px" h="60px" color="gray.300" mb="20px" />
              <Text color="gray.500" fontSize="lg" textAlign="center">
                Select an API from the list on the left to view its details,
                configure parameters, and execute requests.
              </Text>
            </Card>
          )}
        </Flex>
      </Grid>
    </Box>
  );
}
